import {
  Button,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import {
  login_clicked,
  login_outcome,
  login_reset_password_clicked,
  login_signup_clicked,
  login_sso_clicked,
  login_ui_viewed,
  membership_login_issue,
} from '@/analytics';
import {
  EAccountCreationPermission,
  ECommunityAccessConfig,
} from '@/graphql-types/globalTypes';
import { useCurrent } from '@/hooks/useCurrent';
import { useMemberManagement } from '@/hooks/useMemberManagement';

import { EmailSentMessage } from './components/EmailSentMessage';
import { ForgotPasswordForm } from './components/ForgotPasswordForm';
import { LogInForm } from './components/LogInForm';
import { SignUpForm } from './components/SignUpForm';
import { EPromptState } from './utils';

export const LogInSignUp: React.FC<{
  initialPromptState?: EPromptState;
  modal?: boolean;
  ssoProvider?: string;
  /** Always show Tradewing email/password (e.g. /login/tradewing). */
  forceTradewingLogin?: boolean;
  conferenceOptions?: {
    useConferenceHeaders: boolean;
    name: string;
  };
}> = (props) => {
  const [resetPassword, setResetPassword] = React.useState(false);
  const [verifyEmail, setVerifyEmail] = React.useState(false);
  const { isActive: isMemberManagementActive } = useMemberManagement();
  const current = useCurrent();

  const allowSignUp =
    current.tenant?.accountCreationPermission ===
    EAccountCreationPermission.ANYONE;

  const isTenantPublic =
    current.tenant?.communityAccessConfig === ECommunityAccessConfig.PUBLIC;
  React.useEffect(() => {
    login_ui_viewed({
      view: props.modal ? 'modal' : 'wall',
      ssoEnabled: !!props.ssoProvider,
      signupEnabled: allowSignUp,
      isPublic: isTenantPublic,
    });
  }, [
    props.modal,
    allowSignUp,
    props.ssoProvider,
    isTenantPublic,
  ]);

  const onClickResetPassword = (): void => {
    login_reset_password_clicked({
      view: props.modal ? 'modal' : 'wall',
      ssoEnabled: !!props.ssoProvider,
      signupEnabled: allowSignUp,
    });
    setResetPassword(true);
  };

  const onSubmitLogin = (): void => {
    login_clicked({
      view: props.modal ? 'modal' : 'wall',
      ssoEnabled: !!props.ssoProvider,
      signupEnabled: allowSignUp,
    });
  };

  const onClickSSO = (): void => {
    login_sso_clicked({
      ssoProvider: props.ssoProvider || 'none',
      view: props.modal ? 'modal' : 'wall',
      signupEnabled: allowSignUp,
    });
  };

  const onSubmitSignUp = (): void => {
    setVerifyEmail(true);
    login_signup_clicked({
      view: props.modal ? 'modal' : 'wall',
      ssoEnabled: !!props.ssoProvider,
    });
  };

  const onLoginOutcome = (
    outcome:
      | 'tw-login-success'
      | 'tw-activate-success'
      | 'tw-activate-error'
      | 'sso-login-success'
      | 'sso-activate-success'
      | 'tw-login-error-missing-input'
      | 'tw-login-error-wrong-pw'
      | 'tw-login-error-wrong-email'
      | 'sso-login-token-error'
      | 'sso-activate-error'
  ): void => {
    login_outcome({
      outcome,
      view: props.modal ? 'modal' : 'wall',
      ssoEnabled: !!props.ssoProvider,
      signupEnabled: allowSignUp,
    });

    if (outcome.includes('error')) {
      membership_login_issue({
        entity_type: 'login',
        entity_id: outcome,
        login_outcome: outcome,
        view: props.modal ? 'modal' : 'wall',
        ssoEnabled: !!props.ssoProvider,
        page_type: 'membership',
        page_section: 'churn_signal',
      });
    }
  };

  const hideTradewingLogin =
    !!current.tenant?.featureFlags?.hideTradewingLogin;

  const loginHeader = props.conferenceOptions?.useConferenceHeaders
    ? `Log in to ${current.tenant?.displayName} to complete your registration for the ${props.conferenceOptions.name}.`
    : undefined;

  const signUpHeader = props.conferenceOptions?.useConferenceHeaders
    ? `Create a new account in ${current.tenant?.displayName} to complete your registration for the ${props.conferenceOptions.name}.`
    : undefined;

  const logInFormProps = {
    acronym: current.tenant?.acronym || undefined,
    onClickResetPassword,
    header: loginHeader,
    ssoProvider: props.ssoProvider,
    hideTradewingLogin,
    forceTradewingLogin: props.forceTradewingLogin,
    onSubmitLogin,
    onLoginOutcome,
    onClickSSO,
    loginContactMessage: current.tenant?.loginContactMessage || undefined,
  };

  return verifyEmail ? (
    <EmailSentMessage
      headerText="We sent you a verification email!"
      detailText="Check your email and click the verification link to finish creating your account."
      center={props.modal}
    />
  ) : resetPassword ? (
    <ForgotPasswordForm
      onClickLogIn={() => setResetPassword(false)}
      modal={props.modal}
    />
  ) : allowSignUp ? (
    <Stack spacing="6" w="full">
      <Tabs
        defaultIndex={props.initialPromptState}
        variant="solid-rounded"
        align={props.modal ? 'center' : undefined}
      >
        <TabList mb="8">
          <Tab>Sign in</Tab>
          <Tab>Sign up</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <LogInForm {...logInFormProps} />
          </TabPanel>
          <TabPanel>
            <SignUpForm header={signUpHeader} onSubmit={onSubmitSignUp} />
          </TabPanel>
        </TabPanels>
      </Tabs>

      {isMemberManagementActive && (
        <NextLink href="/memberships">
          <Button
            w="full"
            display={['inherit', 'none']}
            variant="outline"
            colorScheme="gray"
          >
            Explore Memberships
          </Button>
        </NextLink>
      )}
    </Stack>
  ) : (
    <LogInForm {...logInFormProps} />
  );
};

LogInSignUp.defaultProps = {
  initialPromptState: EPromptState.LOGIN_FORM,
};
